// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/subsystems/workspaces'
import { useWorkspaceConfig } from '@/core'
import {
  LayoutBuilder as UIBuilder,
  type LayoutBuilderHandle as UIBuilderHandle,
  type UILayout,
  emitLayoutRefresh,
} from '@/subsystems/layout'
import { getServices } from '@/app-shell/services'

export default function LayoutPage() {
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const uiBuilderRef = useRef<UIBuilderHandle>(null)

  const {
    layout: initialLayout,
    loading,
    error,
  } = useWorkspaceConfig(selectedOrganizationId ?? undefined, selectedWorkspaceId ?? undefined)

  const [uiLayout, setUiLayout] = useState<UILayout>(initialLayout ?? { tabs: [] })

  useEffect(() => {
    setUiLayout(initialLayout ?? { tabs: [] })
  }, [initialLayout])

  const handleSaveUILayout = useCallback(async (newLayout: UILayout) => {
    if (!selectedOrganizationId || !selectedWorkspaceId) {
      toast.error('Cannot save: Missing organization or workspace')
      return
    }

    try {
      const services = getServices()
      const savedLayout = await services.layout.saveLayout(selectedOrganizationId, selectedWorkspaceId, newLayout)
      setUiLayout(savedLayout)
      emitLayoutRefresh()
      toast.success('UI layout saved')
    } catch (err) {
      console.error('Failed to save UI layout:', err)
      toast.error('Failed to save UI layout')
      throw err
    }
  }, [selectedOrganizationId, selectedWorkspaceId])

  if (!selectedOrganizationId || !selectedWorkspaceId) {
    return (
      <div className="flex h-64 items-center justify-center px-6 text-center">
        <p className="text-[13px] text-slate-500">
          Select an organization and workspace to use the layout builder.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-[13px] text-slate-500">Loading layout…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center px-6">
        <div className="text-center">
          <p className="text-[13.5px] font-[550] text-red-600">Failed to load layout.</p>
          <p className="mt-1 text-[12.5px] text-slate-500">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-slate-50">
      <UIBuilder
        ref={uiBuilderRef}
        workspaceId={selectedWorkspaceId}
        organizationId={selectedOrganizationId}
        layout={uiLayout}
        onSave={handleSaveUILayout}
      />
    </div>
  )
}
